import { createSlice } from '@reduxjs/toolkit'
import { arrayMove } from '@dnd-kit/sortable'

import {
  getCertifications,
  getCertification,
  createCertification,
  updateCertification,
  updateCertificationCoverImage,
  createSection,
  updateSection,
  deleteSection,
  createLesson,
  updateLesson,
  deleteLesson,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  createCoupon,
  updateCoupon,
  deleteCoupon,
  createPrice,
  updatePrice,
  deletePrice,
} from '../actions/certificationActions'

const initialState = {
  certifications: [],
  certification: null,
  lastSavedAt: null,
}

const renumber = (items) => items.forEach((item, index) => { item.position = index + 1 })

const moveItem = (items, activeId, overId) => {
  const reordered = arrayMove(items, items.findIndex(item => item.id === activeId), items.findIndex(item => item.id === overId))

  renumber(reordered)
  return reordered
}

const findSection = (state, sectionId) => state.certification.sections.find(section => section.id === sectionId)

const findQuiz = (state, quizId) => state.certification.finalQuiz.id === quizId
  ? state.certification.finalQuiz
  : state.certification.sections.map(section => section.quiz).find(quiz => quiz?.id === quizId)

const replaceById = (items, item) => { items[items.findIndex(current => current.id === item.id)] = item }

const isBuilderSave = (action) =>
  action.type.startsWith('certificationBuilder/') && action.type.endsWith('/fulfilled') && !action.type.includes('/get')

const certificationBuilderSlice = createSlice({
  name: 'certificationBuilder',
  initialState,
  reducers: {
    resetCertificationBuilder: (state) => { state.certification = null; state.lastSavedAt = null },
    reorderSections: (state, action) => {
      const { activeId, overId } = action.payload

      state.certification.sections = moveItem(state.certification.sections, activeId, overId)
    },
    reorderLessons: (state, action) => {
      const { sectionId, activeId, overId } = action.payload
      const section = findSection(state, sectionId)

      section.lessons = moveItem(section.lessons, activeId, overId)
    },
    reorderQuestions: (state, action) => {
      const { quizId, activeId, overId } = action.payload
      const quiz = findQuiz(state, quizId)

      quiz.questions = moveItem(quiz.questions, activeId, overId)
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getCertifications.fulfilled, (state, action) => {
        state.certifications = action.payload.certifications
      })
      .addCase(getCertification.fulfilled, (state, action) => {
        state.certification = action.payload.certification
        state.lastSavedAt = action.payload.certification.updatedAt
      })
      .addCase(createCertification.fulfilled, (state, action) => {
        state.certifications.push(action.payload.certification)
      })
      .addCase(updateCertification.fulfilled, (state, action) => {
        state.certification = action.payload.certification
      })
      .addCase(updateCertificationCoverImage.fulfilled, (state, action) => {
        state.certification = action.payload.certification
      })
      .addCase(createSection.fulfilled, (state, action) => {
        state.certification.sections.push(action.payload.section)
      })
      .addCase(updateSection.fulfilled, (state, action) => {
        const { section } = action.payload

        Object.assign(findSection(state, section.id), { title: section.title, quiz: section.quiz })
      })
      .addCase(deleteSection.fulfilled, (state, action) => {
        state.certification.sections = state.certification.sections.filter(section => section.id !== action.payload)
        renumber(state.certification.sections)
      })
      .addCase(createLesson.fulfilled, (state, action) => {
        const { lesson } = action.payload

        findSection(state, lesson.sectionId).lessons.push(lesson)
      })
      .addCase(updateLesson.fulfilled, (state, action) => {
        const { lesson } = action.payload
        const section = findSection(state, lesson.sectionId)

        Object.assign(section.lessons.find(current => current.id === lesson.id), lesson, { position: undefined })
        renumber(section.lessons)
      })
      .addCase(deleteLesson.fulfilled, (state, action) => {
        const section = findSection(state, action.payload.sectionId)

        section.lessons = section.lessons.filter(lesson => lesson.id !== action.payload.id)
        renumber(section.lessons)
      })
      .addCase(createQuestion.fulfilled, (state, action) => {
        const { question } = action.payload

        findQuiz(state, question.quizId).questions.push(question)
      })
      .addCase(updateQuestion.fulfilled, (state, action) => {
        const { question } = action.payload
        const quiz = findQuiz(state, question.quizId)

        Object.assign(quiz.questions.find(current => current.id === question.id), question, { position: undefined })
        renumber(quiz.questions)
      })
      .addCase(deleteQuestion.fulfilled, (state, action) => {
        const quiz = findQuiz(state, action.payload.quizId)

        quiz.questions = quiz.questions.filter(question => question.id !== action.payload.id)
        renumber(quiz.questions)
      })
      .addCase(createCoupon.fulfilled, (state, action) => {
        state.certification.coupons.push(action.payload.coupon)
      })
      .addCase(updateCoupon.fulfilled, (state, action) => {
        replaceById(state.certification.coupons, action.payload.coupon)
      })
      .addCase(deleteCoupon.fulfilled, (state, action) => {
        state.certification.coupons = state.certification.coupons.filter(coupon => coupon.id !== action.payload)
      })
      .addCase(createPrice.fulfilled, (state, action) => {
        state.certification.prices.push(action.payload.price)
      })
      .addCase(updatePrice.fulfilled, (state, action) => {
        replaceById(state.certification.prices, action.payload.price)
      })
      .addCase(deletePrice.fulfilled, (state, action) => {
        state.certification.prices = state.certification.prices.filter(price => price.id !== action.payload)
      })
      .addMatcher(isBuilderSave, (state) => { state.lastSavedAt = new Date().toISOString() })
  },
})

export const { resetCertificationBuilder, reorderSections, reorderLessons, reorderQuestions } = certificationBuilderSlice.actions

export default certificationBuilderSlice.reducer
